import * as React from 'react';
import { IEnvironment } from '../../interfaces/IEnvironment';
import { IInstance } from '../../interfaces/IInstance';
import { ILike } from '../../interfaces/ILike';
import { IUser } from '../../interfaces/IUser';
import { IAComments } from '../comments/Comments';
import { IALikes } from '../likes/Likes';
import * as styles from "./CompactListElement.css";
import { IMessageCategory, FieldToHide } from '../../interfaces/IMessage';
import { IAMessageCategory } from '../viewer/categories/MessageCategory';
import { IFontSlots } from '../../interfaces/IFontSlots';
import Moment from 'react-moment';
import { ENTER_KEY, SPACEBAR_KEY } from '../../global/Constants';

export interface ICompactListElementProps {
  /* Shared */
  backgroundColor?: string;
  textColor?: string;
  highlightColor?: string;
  borderColor?: string;
  showAuthor?: boolean;
  showCategories?: boolean;
  borderRadius?: number;
  showDate?: boolean;
  showTime?: boolean;
  showDropShadow?: boolean;
  category?: IMessageCategory;
  hasCategories?: boolean;
  isMobile?: boolean;
  width?: number;
  author?: string;
  date?: Date | string;
  title?: string;
  id?: number;
  widthTopMinusFromContentArea?: number;
  reactionOptions: string[];
  showEmojiPickerExpandButton: boolean;
  /* Message related */
  showComments?: boolean;
  showLikes?: boolean;
  displayUnread?: boolean;
  instance?: IInstance;
  commentsEnabled?: boolean;
  disableComments?: boolean;
  disableLikes?: boolean;
  likesEnabled?: boolean;
  linkColor?: string;
  user?: IUser;
  tenant?: string;
  component?: string;
  token?: string;
  environment?: IEnvironment;
  likes?: ILike[];
  commentsCount?: number;
  source?: string;
  commentsClick?: () => void;
  onClick?: () => void;
  likesUpdated?: (likes: ILike[]) => void;
  commentsUpdated?: (comments: number) => void;
  showLikesDimmed?: boolean;
  showCommentsDimmed?: boolean;
  fontSlots?: IFontSlots;
  userLocation?: string;
  fieldsToHide?: FieldToHide[];
}


export class CompactListElement extends React.Component<ICompactListElementProps> {

  public render(): JSX.Element {
    const showCategories = this.props.hasCategories && this.props.width > 667 && !this.props.isMobile && this.props.showCategories;
    let widthToMinusFromTitle = 0;
    if (this.props.showDate && this.props.date) {
      widthToMinusFromTitle += 121;
    }
    if (showCategories) {
      widthToMinusFromTitle += 124;
    }
    return (
      <div
        key={this.props.id.toString()}
        className={styles.IA_compactListElement}
        style={{
          padding: (this.props.backgroundColor || this.props.borderColor) ? 9 : 0,
          backgroundColor: this.props.backgroundColor,
          borderRadius: this.props.borderRadius,
          border: `1px solid ${this.props.borderColor ? this.props.borderColor : "transparent"}`,
          boxShadow: this.props.showDropShadow ? "rgba(0, 0, 0, 0.133) 0px 1.6px 3.6px 0px, rgba(0, 0, 0, 0.11) 0px 0.3px 0.9px 0px" : "none",
          borderLeft: this.props.displayUnread ? `6px solid ${this.props.instance?.settings?.highlightUnread ? this.props.instance?.settings?.highlightUnread : "#ffb900"}` : `1px solid ${this.props.borderColor ? this.props.borderColor : "transparent"}`
        }}
      >
        {/* Content */}
        <div
          className={`${styles.IA_compactListElementContent} IA_OnFocus`}
          tabIndex={0}
          style={{
            width: `calc(100% - ${this.props.widthTopMinusFromContentArea + 30}px)`
          }}
          onClick={() => this.props.onClick()}
          onKeyDown={(event) => {
            if (event.key === ENTER_KEY || event.key === SPACEBAR_KEY) {
              event.stopPropagation();
              if (this.props.onClick) {
                this.props.onClick();
              }
              event.currentTarget.blur();
            }
          }}
        >
          {/* Date */}
          {this.props.showDate && this.props.date &&
            <div
              className={styles.IA_compactListElementDate}
              style={{
                color: this.props.textColor,
                fontWeight: this.props.displayUnread ? "bold" : "normal",
                fontFamily: this.props.fontSlots?.body?.fontFamily
              }}
            >
              <Moment
                format="L"
                locale={this.props.userLocation ?? navigator.language}
              >
                {this.props.date}
              </Moment>
              <span> </span>
              {this.props.showTime &&
                <Moment
                  format="LT"
                  locale={this.props.userLocation ?? navigator.language}
                >
                  {this.props.date}
                </Moment>
              }
            </div>
          }
          {/* Author */}
          {!this.props?.fieldsToHide?.includes("author") && this.props.showAuthor && this.props.author &&
            <div
              className={styles.IA_compactListElementAuthor}
              style={{
                color: this.props.textColor,
                float: this.props.isMobile ? "left" : "right",
                textAlign: this.props.isMobile ? "left" : "right",
                fontWeight: this.props.displayUnread ? "bold" : "normal",
                fontFamily: this.props.fontSlots?.body?.fontFamily
              }}
            >
              {this.props.author}
            </div>
          }
          {/* Categories */}
          {showCategories &&
            <div
              className={styles.IA_compactListElementCategory}
              style={{
                float: this.props.isMobile ? "left" : "right",
                flexDirection: this.props.isMobile ? "row" : "row-reverse",
                fontFamily: this.props.fontSlots?.body?.fontFamily
              }}>
              <IAMessageCategory
                category={this.props.category}
                useTransparentAsDefaultBackgroud={!this.props.category?.bgColor}
                allowOnlyOneLineOfText
                textColorWhenTransparentBackground={this.props.textColor}
                isLastCategory
              />
            </div>
          }
          {/* Title */}
          <div
            className={styles.IA_compactListElementTitle}
            style={{
              width: this.props.isMobile || this.props.width < 400 ? "100%" : `calc(100% - ${widthToMinusFromTitle}px)`,
              color: this.props.textColor,
              clear: this.props.isMobile ? "left" : "none",
              fontWeight: this.props.displayUnread ? "bold" : "normal",
              fontFamily: this.props.fontSlots?.heading?.fontFamily
            }}
          >
            {this.props.title}
          </div>
        </div>
        {this.props.widthTopMinusFromContentArea !== 0 &&
          <div
            className={styles.IA_compactListElementSocial}
            style={{
              height: this.props.isMobile ? 38 : 22,
              borderColor: `${this.props.textColor}20`
            }}
          >
            {/* Comments */}
            {this.props.commentsEnabled && this.props.id != undefined && this.props.showComments && !this.props.disableComments && (
              <IAComments
                color={this.props.linkColor}
                itemId={this.props.id.toString()}
                user={this.props.user}
                tenant={this.props.tenant}
                component={this.props.component}
                instance={this.props.instance.instanceName}
                token={this.props.token}
                environment={this.props.environment}
                commentCount={this.props.commentsCount}
                commentsUpdated={(comments: number) => this.props.commentsUpdated(comments)}
                style={{
                  float: "right",
                  marginTop: this.props.isMobile ? 11 : 4,
                }}
                commentsClick={() => { this.props.commentsClick() }}
                showDimmed={this.props.showCommentsDimmed}
              />
            )}
            {/* Likes */}
            {this.props.likesEnabled && this.props.id != undefined && this.props.showLikes && !this.props.disableLikes && (
              <IALikes
                highlightColor={this.props.linkColor}
                color={this.props.textColor}
                itemId={this.props.id.toString()}
                user={this.props.user}
                tenant={this.props.tenant}
                component={this.props.component === "Messages" ? "Message" : "Event"}
                instance={this.props.instance.instanceName}
                token={this.props.token}
                environment={this.props.environment}
                source={this.props.source}
                likes={this.props.likes}
                showEmojiPickerExpandButton={this.props.showEmojiPickerExpandButton}
                reactionOptions={this.props.reactionOptions}
                likesUpdated={(likes: ILike[]) => this.props.likesUpdated(likes)}
                style={{
                  float: "right",
                  marginRight: 6,
                  marginTop: this.props.isMobile ? 10 : 4,
                }}
                showDimmed={this.props.showLikesDimmed}
                isMobile={this.props.isMobile}
              />
            )}
          </div>
        }
      </div >
    );
  }

}